import {
  UserType,
  UpdateUserType,
  UserWithHmacType,
  UpdateUserOrgType,
  UpdateCurrentUserOrgStatusType,
  UpdateRoleType,
  UpdateUsersTeams,
  UserInviteResult,
  BulkInviteRequest,
} from 'src/@types/user';
import { useDelete, useGet, usePut } from './templateHooks';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useAxios } from './useFetch';
import { FAST_API_URL } from 'src/config';
import { Teammate } from 'src/@types/teammates';
import { OrgTypeWithRole } from 'src/@types/org';
import { roleHasPermission } from 'src/auth/rbac';
import { useUserRole } from 'src/hooks/useUserRole';

export const useUser = (config = {}) =>
  useGet<UserWithHmacType>({
    queryKey: ['user'],
    endpoint: '/v1/me',
    config: config,
    url: FAST_API_URL,
  });

// The API refuses the teammate list to volunteers and supporters, so roles without
// `teammates/list` skip the request instead of retrying a 401.
export const useTeammates = (config: { enabled?: boolean } = {}) => {
  const { data: role } = useUserRole();
  return useGet<Teammate[]>({
    queryKey: ['user', 'teammates'],
    endpoint: '/v1/teammates',
    config: {
      ...config,
      enabled: roleHasPermission('teammates/list', role) && (config.enabled ?? true),
    },
    url: FAST_API_URL,
  });
};

// used for updating and registration
// set has_registered to true for registration
export const useUpdateUser = (config = {}) =>
  usePut<UpdateUserType, UserType>(['user'], '/v1/me', config, undefined, FAST_API_URL);

export const useUpdateRole = (onSettled: () => void) => {
  const queryClient = useQueryClient();
  const updateRole = useAxios<Teammate, UpdateRoleType>({
    endpoint: '/v1/update-role',
    method: 'put',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn: updateRole,
    onSuccess: (updatedUserOrg: Teammate) => {
      queryClient.setQueryData<Teammate[]>(['user', 'teammates'], (old) => {
        if (!old) return [];
        return [
          ...old.map((user) =>
            user.email === updatedUserOrg.email
              ? { ...user, user_org_role: updatedUserOrg.user_org_role }
              : user
          ),
        ];
      });
    },
    onSettled,
  });
};

export const useRemoveUser = () =>
  useDelete<{ ids: string[] }, string, Teammate>(
    ['user', 'teammates'],
    '/v1/users-orgs',
    FAST_API_URL
  );

export function useUpdateTeammateTeams(userId: string) {
  const updateUsersTeamsMutation = usePut<UpdateUsersTeams, undefined>(
    ['user', 'teammates'],
    '/v2/users/' + userId + '/teams',
    undefined,
    undefined,
    FAST_API_URL
  );
  return updateUsersTeamsMutation;
}

export const useUpdateUserOrg = (userOrgId: string) => {
  const queryClient = useQueryClient();
  const updateUserOrg = useAxios<Teammate, UpdateUserOrgType>({
    endpoint: '/v1/users-orgs/' + userOrgId,
    method: 'put',
    url: FAST_API_URL,
  });

  return useMutation({
    mutationFn: updateUserOrg,
    onSuccess: (updatedUserOrg) => {
      queryClient.setQueryData<Teammate[]>(['user', 'teammates'], (old) => {
        const newTeammates = old?.map((t) => {
          if (t.user_org_id === userOrgId) return { ...t, ...updatedUserOrg };
          return t;
        });
        return newTeammates;
      });
    },
  });
};

export const useUpdateCurrentUserOrgStatus = (orgId: string) => {
  const queryClient = useQueryClient();
  const updateCurrentUserOrgStatus = useAxios<undefined, UpdateCurrentUserOrgStatusType>({
    endpoint: `/v1/orgs/${orgId}/invite-status`,
    method: 'put',
    url: FAST_API_URL,
  });

  return useMutation({
    mutationFn: updateCurrentUserOrgStatus,
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries({ queryKey: ['user', 'orgs'] });

      const previousUserOrgs = queryClient.getQueryData<OrgTypeWithRole[]>(['user', 'orgs']);

      if (!data) {
        return { previousUserOrgs };
      }

      queryClient.setQueryData<OrgTypeWithRole[]>(
        ['user', 'orgs'],
        (old) =>
          old?.map((org) =>
            org.id === orgId ? { ...org, invite_status: data.invite_status } : org
          ) ?? old
      );

      return { previousUserOrgs };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUserOrgs) {
        queryClient.setQueryData(['user', 'orgs'], context.previousUserOrgs);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['user'] });
      void queryClient.invalidateQueries({ queryKey: ['user', 'orgs'] });
    },
  });
};

export const useInviteUsers = () => {
  const queryClient = useQueryClient();
  const inviteUsers = useAxios<UserInviteResult[], BulkInviteRequest>({
    endpoint: '/v1/users-orgs/bulk-user-invite',
    method: 'post',
    url: FAST_API_URL,
  });

  return useMutation({
    mutationFn: inviteUsers,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user', 'teammates'] });
    },
  });
};

export const useReInviteUsers = (userId: string) => {
  const reInviteUsers = useAxios<undefined, undefined>({
    endpoint: `/v1/users-orgs/users/${userId}/resend-invite`,
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({ mutationFn: reInviteUsers });
};
